// packages imports
import { Fragment } from 'react'
import { useFormContext } from 'react-hook-form'
import { Divider, Grid, Typography, TextField, Box } from '@mui/material'

// components imports
import CheckboxController from '../../../common/CheckboxController'
import { CustomController } from '../../../common/CustomController'

// others imports
import { ExternalUserWithClientNumberInterface } from '../../../../interfaceTypes'
import { enumToLabel, titleize } from '../../../../utils'
import { ExternalUserCaseRole } from '../../../../../generated'
import { InfoOutlined } from '@mui/icons-material'
import palette from '../../../../theme/palette'

const BillSplitForm = () => {
  const { watch } = useFormContext()
  const splitBillEnabled = watch('billing.splitBillEnabled')

  const primaryClient = { ...watch('client'), caseRole: ExternalUserCaseRole.PrimaryClient }
  const clientUsers: ExternalUserWithClientNumberInterface[] = watch('clientUsers')?.concat(primaryClient) || []
  const isSplitShareFull =
    clientUsers
      .filter(user => user?.isBillParticipant)
      .reduce((sum, user) => sum + (Number(user.splitBillShare) || 0), 0) === 100

  return (
    <Grid container item rowSpacing={2.5}>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" textTransform="uppercase" fontWeight={700}>
          Bill Split
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <CheckboxController name="billing.splitBillEnabled" label="Share Bill Among Client Parties" sx={{ mt: -1 }} />
      </Grid>

      {splitBillEnabled && (
        <Grid container item spacing={2.5} alignItems="center">
          {clientUsers.map((client: ExternalUserWithClientNumberInterface, index: number) => {
            const caseRole = titleize(client.caseRole?.replace('_', '-') || '')

            if (!client.id || !client.caseRole) {
              return null
            }

            return (
              <Fragment key={`${client.id}-${caseRole}`}>
                <Grid item xs={2} md={1}>
                  <CheckboxController
                    name={
                      client.caseRole === ExternalUserCaseRole.PrimaryClient
                        ? `client.isBillParticipant`
                        : `clientUsers.${index}.isBillParticipant`
                    }
                    label=""
                  />
                </Grid>
                <Grid item xs={10} md={4}>
                  <TextField
                    value={`${client.firstName} ${client.lastName}`}
                    label="Client"
                    variant="outlined"
                    disabled={!clientUsers[index]?.isBillParticipant}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3.5}>
                  <TextField
                    value={enumToLabel(caseRole)}
                    label="Case Role"
                    variant="outlined"
                    disabled={!clientUsers[index]?.isBillParticipant}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={6} md={3.5}>
                  <CustomController
                    controllerLabel="Share (%)"
                    controllerName={
                      client.caseRole === ExternalUserCaseRole.PrimaryClient
                        ? `client.splitBillShare`
                        : `clientUsers.${index}.splitBillShare`
                    }
                    fieldType="number"
                    defaultValue={'0'}
                    isDisabled={!clientUsers[index]?.isBillParticipant}
                    sx={{ my: 0 }}
                  />
                </Grid>
              </Fragment>
            )
          })}
          {!isSplitShareFull && (
            <Box display={'flex'} gap={1} alignItems={'center'} mt={2} ml={9}>
              <InfoOutlined color="primary" fontSize={'inherit'} />
              <Typography color={palette.primary.main} fontSize={12}>
                Total Bill Share (%) for selected clients must be 100
              </Typography>
            </Box>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default BillSplitForm
